import React, { useEffect, useRef } from "react";
import { Card, CardHeader, CardBody, CardFooter, Button } from "reactstrap";
import { Chart } from "chart.js/auto";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import { convertPenniesToDecimals } from "src/utils/money";
import {
  richTooltipConfig,
  richAnimationConfig,
} from 'src/config/chart';
import millify from 'millify';

const TrafficDougnut = ({ data, loading, chartFilter, setChartFilter }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (loading === false && canvasRef.current) {
      // Destroy previous chart if exists
      if (chartRef.current) {
        chartRef.current.destroy();
      }

      const ctx = canvasRef.current.getContext("2d");

      var config = {
        type: "bar",
        data: {
          labels: data.by_month.map((item) => moment(item.date_from).format("MMM YY")),
          datasets: [
            {
              label: 'Supplier Paid',
              backgroundColor: '#a78bfa',
              hoverBackgroundColor: '#8b5cf6',
              borderRadius: 4,
              borderSkipped: false,
              data: data.by_month.map((item) => Math.floor(convertPenniesToDecimals(item[chartFilter]) * 0.75)),
            },
            {
              label: 'Held By Agent',
              backgroundColor: '#581c87',
              hoverBackgroundColor: '#6b21a8',
              borderRadius: 4,
              borderSkipped: false,
              data: data.by_month.map((item) => Math.floor(convertPenniesToDecimals(item[chartFilter]) * 0.8)),
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              align: 'end',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 16,
                color: '#64748b',
                font: {
                  size: 11,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
              },
            },
            tooltip: {
              ...richTooltipConfig,
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  return ` ${context.dataset.label}: £${new Intl.NumberFormat('en-GB').format(value)}`;
                },
              },
            },
          },
          animation: richAnimationConfig,
          scales: {
            x: {
              stacked: true,
              grid: {
                display: false,
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 10,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 8,
                maxRotation: 0,
              },
            },
            y: {
              stacked: true,
              grid: {
                display: true,
                color: 'rgba(148, 163, 184, 0.08)',
                drawBorder: false,
              },
              border: {
                display: false,
              },
              ticks: {
                color: '#94a3b8',
                font: {
                  size: 11,
                  family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                },
                padding: 12,
                maxTicksLimit: 6,
                callback: function (value) {
                  return `£${millify(value)}`;
                }
              },
            },
          },
        },
      };

      chartRef.current = new Chart(ctx, config);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    }
  }, [loading, data, chartFilter]);

  if (loading) {
    return (
      <Card className="flex min-h-[400px] flex-col rounded-md border animate-in fade-in-50">
        <CardHeader className="bg-gray-50 mb-2 text-purple-900">
          <h2 className="text-xl text-purple-900 font-bold">
            Forward Looking Untraveled Bookings
          </h2>
          <p className="text-xs">
            Un-travelled bookings by month, grouped by whether supplier has been paid. {" "}
          </p>
        </CardHeader>
        <CardBody className="card-inner pt-0">
          <div className="traffic-channel">
            <Skeleton count={1} height="250px" />
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="flex min-h-[400px] flex-col rounded-md border animate-in fade-in-50">
      <CardHeader className="bg-gray-50 mb-2 text-purple-900">
        <h2 className="text-xl text-purple-900 font-bold">
          Forward Looking Untraveled Bookings
        </h2>
        <p className="text-xs">
          Un-travelled bookings by month, grouped by whether supplier has been paid. {" "}
        </p>
      </CardHeader>
      <CardBody className="card-inner pt-0">
        <div className="pt-4 grow max-h-[250px]">
          <canvas ref={canvasRef}></canvas>
        </div>
      </CardBody>
      <CardFooter className="bg-light">
        <Button
          className={`float-right ml-2 ${chartFilter === 'total' ? 'bg-slate-800' : 'bg-purple-800'}`}
          onClick={() => setChartFilter('total')}
        >
          All Card Types
        </Button>
        <Button
          className={`float-right ml-2 ${chartFilter === 'credit' ? 'bg-slate-800' : 'bg-purple-800'}`}
          onClick={() => setChartFilter('credit')}
        >
          Credit Cards
        </Button>
        <Button
          className={`float-right ml-2 ${chartFilter === 'debit' ? 'bg-slate-800' : 'bg-purple-800'}`}
          onClick={() => setChartFilter('debit')}
        >
          Debit Cards
        </Button>
      </CardFooter>
    </Card>
  );
};
export default TrafficDougnut;
